// src/components/Admin/ManageAnnouncements.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import Overlay from '../Overlay';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage2';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';


function ManageAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [formState, setFormState] = useState({
    title: '',
    date: '',
    body: '',
    link: '',
    imageFile: null,
    imageURL: '',
    id: null, // 編集時に使用
  });
  const [preview, setPreview] = useState(null); // プレビュー用
  const [showOverlay, setShowOverlay] = useState(false);

  // トリミング用の状態
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const snapshot = await db.collection("announcements").orderBy("date", "desc").get();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAnnouncements(data);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      alert('お知らせの取得に失敗しました。');
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormState(prev => ({ ...prev, imageFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setIsCropping(true); // トリミングモードに切り替え
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setIsCropping(false);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const { blob, fileUrl } = await getCroppedImg(preview, croppedAreaPixels, zoom, 0.7, 600, 864); // aspect=1.5 (300/200)
      setCroppedImage(blob);
      setPreview(fileUrl); // プレビューをトリミング後の画像に更新
      setIsCropping(false);
    } catch (e) {
      console.error(e);
      alert('画像のトリミングに失敗しました。');
    }
  }, [preview, croppedAreaPixels, zoom]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (formState.id) {
      await updateAnnouncement();
    } else {
      await addAnnouncement();
    }
  };

  const addAnnouncement = async () => {
    try {
      let imageURL = '';
      if (croppedImage) {
        const storageRef = storage.ref(`announcements/${Date.now()}_${formState.imageFile.name}`);
        await storageRef.put(croppedImage);
        imageURL = await storageRef.getDownloadURL();
      }

      await db.collection("announcements").add({
        title: formState.title,
        date: new Date(formState.date),
        body: formState.body,
        link: formState.link,
        imageURL,
      });

      alert('お知らせが追加されました。');
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error("Error adding announcement:", error);
      alert('お知らせの追加に失敗しました。');
    }
  };

  const updateAnnouncement = async () => {
    try {
      let imageURL = formState.imageURL;
      if (croppedImage) {
        const storageRef = storage.ref(`announcements/${Date.now()}_${formState.imageFile.name}`);
        await storageRef.put(croppedImage);
        imageURL = await storageRef.getDownloadURL();
      }

      await db.collection("announcements").doc(formState.id).update({
        title: formState.title,
        date: new Date(formState.date),
        body: formState.body,
        link: formState.link,
        imageURL,
      });

      alert('お知らせが更新されました。');
      resetForm();
      setShowOverlay(false);
      fetchAnnouncements();
    } catch (error) {
      console.error("Error updating announcement:", error);
      alert('お知らせの更新に失敗しました。');
    }
  };

  const deleteAnnouncement = async (id, imageURL) => {
    if (!window.confirm('このお知らせを削除してもよろしいですか？')) return;
    try {
      await db.collection("announcements").doc(id).delete();
      if (imageURL) {
        const storageRef = storage.refFromURL(imageURL);
        await storageRef.delete();
      }
      alert('お知らせが削除されました。');
      fetchAnnouncements();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      alert('お知らせの削除に失敗しました。');
    }
  };

  const editAnnouncement = announcement => {
    setFormState({
      title: announcement.title,
      date: announcement.date.toDate().toISOString().split('T')[0],
      body: announcement.body,
      link: announcement.link || '',
      imageFile: null,
      imageURL: announcement.imageURL || '',
      id: announcement.id,
    });
    setPreview(announcement.imageURL || null);
    setCroppedImage(null);
    setIsCropping(false);
  };

  const viewAnnouncement = announcement => {
    setFormState({
      title: announcement.title,
      date: announcement.date.toDate().toISOString().split('T')[0],
      body: announcement.body,
      link: announcement.link || '',
      imageFile: null,
      imageURL: announcement.imageURL || '',
      id: announcement.id,
    });
    setShowOverlay(true);
  };

  const resetForm = () => {
    setFormState({
      title: '',
      date: '',
      body: '',
      link: '',
      imageFile: null,
      imageURL: '',
      id: null,
    });
    setPreview(null);
    setCroppedImage(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="manage-announcements">
      <h2>お知らせの管理</h2>
      <form onSubmit={handleSubmit}>
        <label>
          タイトル:
          <input 
            type="text" 
            name="title" 
            value={formState.title} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          日付:
          <input 
            type="date" 
            name="date" 
            value={formState.date} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          本文:
          <textarea 
            name="body" 
            value={formState.body} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          リンク:
          <input 
            type="url" 
            name="link" 
            value={formState.link} 
            onChange={handleInputChange} 
          />
        </label>
        <label>
          画像:
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
          />
        </label>

        {isCropping && preview && (
          <div className="crop-container">
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={1 / 1} // 300 / 200 = 1.5
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="controls">
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom)}
                style={{ width: '200px' }}
              />
              <Button variant="contained" color="primary" onClick={showCroppedImage} style={{ marginLeft: '10px' }}>
                トリミング
              </Button>
            </div>
          </div>
        )}

        {preview && !isCropping && (
          <div className="preview">
            <p>プレビュー:</p>
            <img 
              src={preview} 
              alt="Preview" 
              style={{ 
                width: '400px', 
                height: '400px', 
                objectFit: 'cover', 
                border: '2px solid #ccc' 
              }} 
            />
          </div>
        )}

        <Button type="submit" variant="contained" color="primary" className="button">
          {formState.id ? 'お知らせを更新' : 'お知らせを追加'}
        </Button>
        {formState.id && (
          <Button 
            type="button" 
            variant="outlined" 
            color="secondary" 
            onClick={resetForm}
            className="button"
            style={{ marginLeft: '10px' }}
          >
            キャンセル
          </Button>
        )}
      </form>

      <h3>既存のお知らせ</h3>
      <ul>
        {announcements.map(announcement => (
          <li key={announcement.id} className="admin-list-item">
            {announcement.imageURL && <img src={announcement.imageURL} alt={announcement.title} style={{ width: '150px', height: '100px', objectFit: 'cover' }} />}
            <div>
              <h4>{announcement.title}</h4>
              <p>{announcement.date.toDate().toLocaleDateString()}</p>
              <Button onClick={() => viewAnnouncement(announcement)} variant="outlined" color="primary" style={{ marginRight: '10px' }}>
                プレビュー
              </Button>
              <Button onClick={() => editAnnouncement(announcement)} variant="outlined" color="secondary" style={{ marginRight: '10px' }}>
                編集
              </Button>
              <Button onClick={() => deleteAnnouncement(announcement.id, announcement.imageURL)} variant="outlined" color="error">
                削除
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {showOverlay && (
        <Overlay onClose={() => setShowOverlay(false)}>
          <h3>{formState.title}</h3>
          <p>{new Date(formState.date).toLocaleDateString()}</p>
          {formState.imageURL && <img src={formState.imageURL} alt={formState.title} style={{ width: '300px', height: '430px', objectFit: 'cover', marginBottom: '1rem' }} />}
          <p>{formState.body}</p>
          {formState.link && <a href={formState.link} target="_blank" rel="noopener noreferrer" className="button">詳細を見る</a>}
        </Overlay>
      )}
    </div>
  );
}

export default ManageAnnouncements;
